import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Badge,
  Collapse,
  Table,
  Modal
} from 'react-bootstrap';
import { 
  FaArrowLeft, 
  FaPlay, 
  FaHistory, 
  FaInfoCircle,
  FaSpinner,
  FaServer,
  FaCode,
  FaList,
  FaChevronDown,
  FaChevronRight,
  FaClock,
  FaDatabase,
  FaInfo,
  FaWrench,
  FaTimes,
  FaCheckCircle
} from 'react-icons/fa';
import * as api from '../services/api';
import Spinner from '../components/Spinner';
import ExecutionLogsWindow from '../components/ExecutionLogsWindow';
import OverallOutputPanel from '../components/OverallOutputPanel';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AgentTestingPage.css';

/**
 * Get default value for a field based on its type
 */
const getDefaultValueForType = (fieldType, fieldSchema) => {
  switch (fieldType) {
    case 'object':
      return {};
    case 'array':
      return [];
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return fieldSchema.default !== undefined ? fieldSchema.default : 0;
    case 'string':
    default:
      return fieldSchema.default !== undefined ? fieldSchema.default : '';
  }
};

/**
 * Initialize nested object with proper defaults based on field types
 */
const initializeNestedObject = (properties, required = []) => {
  const nestedObj = {};
  
  Object.keys(properties).forEach(nestedKey => {
    const nestedProp = properties[nestedKey];
    nestedObj[nestedKey] = getDefaultValueForType(nestedProp.type, nestedProp);
  });
  
  return nestedObj;
};

/**
 * Enhanced schema parser to handle multiple formats:
 * 1. Plain JSON string with various schema structures
 * 2. Markdown-formatted schema with ```json blocks
 * 3. Schema with "Input Schema" and "Output Schema" sections
 * 4. Schema with input/output properties
 * 5. Schema with input_schema/output_schema properties
 * 6. Plain object schemas without wrapper structures
 * 7. Empty or null schemas
 */
const parseSchemaFromString = (schemaString) => {
  // Handle empty or null schemas
  if (!schemaString || schemaString.trim() === '' || schemaString === '{}') {
    console.log('Schema is empty or null, returning default schema');
    return {
      type: 'object',
      properties: {
        data: {
          type: 'string',
          title: 'Test Data',
          description: 'Enter test data for the service'
        }
      },
      required: []
    };
  }

  console.log('Parsing schema string, first 200 chars:', schemaString.substring(0, 200));
  
  // First, try to handle markdown-formatted schemas
  const jsonBlockRegex = /```json\s*([^`]*?)```/g;
  const matches = [...schemaString.matchAll(jsonBlockRegex)];
  
  if (matches.length > 0) {
    console.log(`Found ${matches.length} markdown JSON blocks`);
    
    // Check for Input/Output Schema labels
    if (schemaString.includes('**Input Schema:**') || 
        schemaString.includes('Input Schema:') ||
        schemaString.includes('**Output Schema:**') ||
        schemaString.includes('Output Schema:')) {
      
      console.log('Detected Input/Output schema structure in markdown');
      
      // Extract input schema - try both bold and non-bold formats
      const inputPatterns = [
        /\*\*Input Schema:\*\*[\s\S]*?```json\s*([^`]*?)```/,
        /Input Schema:[\s\S]*?```json\s*([^`]*?)```/
      ];
      
      for (const pattern of inputPatterns) {
        const inputMatch = schemaString.match(pattern);
        if (inputMatch && inputMatch[1]) {
          try {
            const inputSchema = JSON.parse(inputMatch[1].trim());
            console.log('Successfully parsed input schema from markdown');
            return inputSchema;
          } catch (e) {
            console.error('Error parsing input schema from pattern:', e);
          }
        }
      }
    }
    
    // If no specific input/output sections found, use the first JSON block
    try {
      const firstBlock = JSON.parse(matches[0][1].trim());
      console.log('Using first JSON block from markdown');
      return processSchemaObject(firstBlock);
    } catch (e) {
      console.error('Error parsing first JSON block:', e);
    }
  }
  
  // No markdown blocks found, try to parse as plain JSON
  console.log('No markdown blocks found, attempting plain JSON parse');
  try {
    const parsed = JSON.parse(schemaString);
    console.log('Successfully parsed as plain JSON');
    return processSchemaObject(parsed);
  } catch (e) {
    console.error('Error parsing as plain JSON:', e);
    throw new Error('Could not parse schema - not valid JSON or markdown format: ' + e.message);
  }
};

/**
 * Process a schema object to extract the appropriate input schema
 * Handles various schema structures:
 * - { input: {...}, output: {...} }
 * - { input_schema: {...}, output_schema: {...} }
 * - { type: "object", properties: {...} } (plain schema)
 * - Complex nested schemas with multiple levels
 */
const processSchemaObject = (schemaObj) => {
  console.log('Processing schema object with keys:', Object.keys(schemaObj));
  
  // Priority 1: Look for input_schema property (highest priority)
  if (schemaObj.input_schema) {
    console.log('Found input_schema property, using it');
    if (typeof schemaObj.input_schema === 'object' && schemaObj.input_schema.properties) {
      return schemaObj.input_schema;
    }
    // If input_schema doesn't have properties, wrap it
    return {
      type: 'object',
      properties: schemaObj.input_schema,
      required: []
    };
  }
  
  // Priority 2: Look for input property
  if (schemaObj.input) {
    console.log('Found input property, processing it');
    
    // Check if input is already a proper schema with properties
    if (typeof schemaObj.input === 'object') {
      if (schemaObj.input.properties) {
        console.log('Input has properties, using as schema');
        return schemaObj.input;
      }
      
      // Check if input has nested structure that might need unwrapping
      if (schemaObj.input.type === 'object' && schemaObj.input.required && schemaObj.input.properties) {
        console.log('Input is a complete schema object');
        return schemaObj.input;
      }
      
      // Input is an object but not a schema - wrap it as properties
      console.log('Input is object but not schema, wrapping as properties');
      return {
        type: 'object',
        properties: schemaObj.input,
        required: []
      };
    }
  }
  
  // Priority 3: Check if this is a direct schema object (has type and properties)
  if (schemaObj.type && schemaObj.properties) {
    console.log('Schema object has type and properties, using directly');
    return schemaObj;
  }
  
  // Priority 4: Check for nested body structure (from API responses)
  if (schemaObj.body && typeof schemaObj.body === 'object') {
    console.log('Found body property, recursively processing');
    return processSchemaObject(schemaObj.body);
  }
  
  // Priority 5: Check if object has properties that could be form fields
  if (typeof schemaObj === 'object' && Object.keys(schemaObj).length > 0) {
    // Check if all values are objects with type/description (potential schema properties)
    const hasSchemaLikeProperties = Object.values(schemaObj).some(val => 
      val && typeof val === 'object' && (val.type || val.properties || val.description)
    );
    
    if (hasSchemaLikeProperties) {
      console.log('Object has schema-like properties, wrapping as schema');
      return {
        type: 'object',
        properties: schemaObj,
        required: []
      };
    }
    
    // Check for complex nested structures (like the deployment schema example)
    const complexKeys = ['required', 'properties', 'coreDeploymentFields', 'agentDeploymentFields'];
    const hasComplexStructure = complexKeys.some(key => schemaObj.hasOwnProperty(key));
    
    if (hasComplexStructure) {
      console.log('Found complex schema structure, processing');
      
      // If it has required and properties at root level, it's a schema
      if (schemaObj.required && schemaObj.properties) {
        return {
          type: 'object',
          properties: schemaObj.properties,
          required: schemaObj.required
        };
      }
      
      // Otherwise, treat the whole object as properties
      return {
        type: 'object',
        properties: schemaObj,
        required: []
      };
    }
  }
  
  // Default fallback - wrap whatever we have as a simple schema
  console.log('Using fallback schema wrapper');
  return {
    type: 'object',
    properties: {
      data: {
        type: 'string',
        title: 'Input Data',
        description: 'Enter input data for the service',
        default: typeof schemaObj === 'string' ? schemaObj : JSON.stringify(schemaObj, null, 2)
      }
    },
    required: []
  };
};

/**
 * Detect and handle schema fields that have conditional requirements
 * (like coreDeploymentFields vs agentDeploymentFields based on deploymentType)
 */
const processConditionalFields = (schema) => {
  if (!schema || !schema.properties) return schema;
  
  const processedSchema = { ...schema };
  const processedProperties = { ...schema.properties };
  
  // Look for conditional field groups
  Object.keys(processedProperties).forEach(key => {
    const prop = processedProperties[key];
    
    // Check for fields that have conditional structure
    if (prop && typeof prop === 'object' && prop.description && 
        prop.description.includes("Required when")) {
      
      console.log(`Found conditional field group: ${key}`);
      
      // If it has sub-properties, flatten them into the main properties
      if (prop.properties) {
        Object.keys(prop.properties).forEach(subKey => {
          const subProp = prop.properties[subKey];
          processedProperties[`${key}_${subKey}`] = {
            ...subProp,
            title: `${key.replace(/([A-Z])/g, ' $1').trim()} - ${subKey}`,
            description: `${prop.description} | ${subProp.description || ''}`
          };
        });
        
        // Remove the original conditional group
        delete processedProperties[key];
      }
    }
  });
  
  processedSchema.properties = processedProperties;
  return processedSchema;
};

/**
 * Enhanced function to determine if a response is successful
 */
const isResponseSuccessful = (result) => {
  if (!result) return false;
  
  // Check various success indicators
  if (result.success === true) return true;
  if (result.endpoint_success === true) return true;
  
  // Check status code
  if (result.endpoint_status === 200) return true;
  if (result.status === 200) return true;
  
  // Check for successful status in nested structures
  if (result.endpoint_response) {
    if (result.endpoint_response.success === true) return true;
    if (result.endpoint_response.status === 200) return true;
  }
  
  // Check for HTTP status codes that indicate success (2xx range)
  const statusCode = result.endpoint_status || result.status || result.statusCode;
  if (statusCode && statusCode >= 200 && statusCode < 300) return true;
  
  // If no explicit success indicators but no explicit failure indicators, assume success
  const hasErrorIndicators = result.error || 
                             result.endpoint_success === false || 
                             result.success === false ||
                             (statusCode && (statusCode >= 400 || statusCode === 0));
  
  if (!hasErrorIndicators && (result.data || result.endpoint_response || result.response)) {
    return true;
  }
  
  return false;
};

/**
 * Enhanced function to get status code from response
 */
const getResponseStatusCode = (result) => {
  if (!result) return null;
  
  // Try various status code locations
  if (result.endpoint_status) return result.endpoint_status;
  if (result.status) return result.status;
  if (result.statusCode) return result.statusCode;
  if (result.endpoint_response && result.endpoint_response.status) return result.endpoint_response.status;
  if (result.endpoint_response && result.endpoint_response.statusCode) return result.endpoint_response.statusCode;
  
  // If we have data but no explicit status, assume 200
  if (result.data || result.endpoint_response || result.response) return 200;
  
  return null;
};

/**
 * Enhanced function to extract response headers
 */
const getResponseHeaders = (result) => {
  if (!result) return null;
  
  // Try various header locations
  if (result.endpoint_response && result.endpoint_response.response_headers) {
    return result.endpoint_response.response_headers;
  }
  if (result.endpoint_response && result.endpoint_response.headers) {
    return result.endpoint_response.headers;
  }
  if (result.headers) {
    return result.headers;
  }
  if (result.response_headers) {
    return result.response_headers;
  }
  
  return null;
};

/**
 * Enhanced function to get the main response body for display
 */
const getResponseBody = (result) => {
  if (!result) return null;
  
  // Try to get the most meaningful response data
  if (result.data) return result.data;
  if (result.endpoint_response) return result.endpoint_response;
  if (result.response) return result.response;
  
  // Return the entire result if we can't find a specific body
  return result;
};

/**
 * Parse the lambda logs response and extract structured data
 */
const parseLambdaLogsResponse = (rawResponse) => {
  try {
    console.log('Parsing lambda logs response:', rawResponse);
    
    let parsedData = rawResponse;
    
    // If the response is a string, try to parse it
    if (typeof rawResponse === 'string') {
      try {
        parsedData = JSON.parse(rawResponse);
      } catch (parseError) {
        console.error('Failed to parse lambda logs as JSON:', parseError);
        return {
          error: 'Failed to parse lambda logs response as JSON',
          rawResponse
        };
      }
    }
    
    // Check for the expected structure
    if (!parsedData || typeof parsedData !== 'object') {
      return {
        error: 'Invalid lambda logs response structure',
        rawResponse: parsedData
      };
    }
    
    // Extract the main components
    let body = null;
    let statusCode = parsedData.statusCode;
    
    // Parse the body if it's a string
    if (parsedData.body && typeof parsedData.body === 'string') {
      try {
        body = JSON.parse(parsedData.body);
      } catch (bodyParseError) {
        console.error('Failed to parse body as JSON:', bodyParseError);
        return {
          error: 'Failed to parse lambda logs body as JSON',
          statusCode,
          rawResponse: parsedData
        };
      }
    } else if (parsedData.body && typeof parsedData.body === 'object') {
      body = parsedData.body;
    }
    
    if (!body) {
      return {
        error: 'No body found in lambda logs response',
        statusCode,
        rawResponse: parsedData
      };
    }
    
    // Extract components from body
    const status = body.status;
    const events = body.data?.events || [];
    const metadata = body.metadata || {};
    const request = body.request || {};
    
    console.log(`Successfully parsed lambda logs: ${events.length} events found`);
    
    return {
      success: true,
      statusCode,
      status,
      events,
      metadata,
      request,
      rawResponse: parsedData
    };
    
  } catch (error) {
    console.error('Error parsing lambda logs response:', error);
    return {
      error: error.message,
      rawResponse
    };
  }
};

/**
 * Format Unix timestamp to readable date/time
 */
const formatTimestamp = (unixTimestamp) => {
  try {
    const date = new Date(unixTimestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
      full: date.toLocaleString(),
      iso: date.toISOString()
    };
  } catch (error) {
    return {
      date: 'Invalid date',
      time: 'Invalid time',
      full: 'Invalid timestamp',
      iso: null
    };
  }
};

/**
 * Clean up log message text by handling escape characters
 */
const cleanLogMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return message;
  }
  
  // Replace escape sequences with actual characters
  return message
    .replace(/\\t/g, '\t')        // Replace \t with actual tab
    .replace(/\\n/g, '\n')        // Replace \n with actual newline
    .replace(/\\r/g, '\r')        // Replace \r with actual carriage return
    .replace(/\\\\/g, '\\')       // Replace \\ with actual backslash
    .replace(/\\\"/g, '"')         // Replace \" with actual quote
    .trim();                      // Remove leading/trailing whitespace
};

const ServiceTestingPage = () => {
  const { agentId, microServiceId } = useParams();
  const navigate = useNavigate();
  
  // State variables
  const [microService, setMicroService] = useState(null);
  const [parentAgent, setParentAgent] = useState(null);
  const [schema, setSchema] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [executingEnvironment, setExecutingEnvironment] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [executionArn, setExecutionArn] = useState(null);
  const [environment, setEnvironment] = useState('development');
  const [formErrors, setFormErrors] = useState({});
  const [liveLogs, setLiveLogs] = useState(null);
  const [fetchingLogs, setFetchingLogs] = useState(false);

  // NEW: State for lambda logs - simplified to just store raw response
  const [lambdaLogs, setLambdaLogs] = useState(null);
  const [fetchingLambdaLogs, setFetchingLambdaLogs] = useState(false);
  
  // NEW: State for controlling lambda logs display
  const [expandedLogSections, setExpandedLogSections] = useState({
    summary: true,
    events: true,
    metadata: false
  });

  // ENHANCED: State for Quick Fix/Enhancement modal with better UX
  const [showQuickFixModal, setShowQuickFixModal] = useState(false);
  const [quickFixInstructions, setQuickFixInstructions] = useState('');
  const [addLambdaLogsToContext, setAddLambdaLogsToContext] = useState(false);
  const [submittingQuickFix, setSubmittingQuickFix] = useState(false);
  const [quickFixResult, setQuickFixResult] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Memoized callback for telemetry to prevent re-renders
  const handlePanelViewed = useCallback(() => {
    // Add telemetry tracking if needed
  }, []);

  // Fetch service details and schema on load
  useEffect(() => {
    fetchMicroServiceDetails();
    fetchSchema();
  }, [microServiceId]);

  // Enhanced helper function to safely extract data from deeply nested response structures
  const extractFromResponse = (response, ...keys) => {
    if (!response) return null;
    
    console.log('Extracting from response structure:', Object.keys(response));
    
    // Enhanced paths to handle the new test_results structure
    const paths = [
      // Direct paths
      response,
      response.response,
      response.body,
      response.data,
      
      // New nested test_results paths
      response.body?.data?.test_results?.endpoint_response,
      response.data?.test_results?.endpoint_response,
      
      // Handle the nested body that might be a JSON string
      (() => {
        try {
          // Check if we have the test_results structure with a string body
          if (response.body?.data?.test_results?.endpoint_response?.body && 
              typeof response.body.data.test_results.endpoint_response.body === 'string') {
            console.log('Found test_results with string body, parsing...');
            return JSON.parse(response.body.data.test_results.endpoint_response.body);
          }
          
          if (response.data?.test_results?.endpoint_response?.body && 
              typeof response.data.test_results.endpoint_response.body === 'string') {
            console.log('Found test_results with string body in data, parsing...');
            return JSON.parse(response.data.test_results.endpoint_response.body);
          }
          
          return null;
        } catch (e) {
          console.warn('Failed to parse nested JSON body:', e);
          return null;
        }
      })()
    ].filter(Boolean);
    
    for (const path of paths) {
      if (!path) continue;
      
      // If path is a string, try to parse it
      let currentPath = path;
      if (typeof currentPath === 'string') {
        try {
          currentPath = JSON.parse(currentPath);
        } catch (e) {
          continue;
        }
      }
      
      // Try to find the requested keys in this path
      let value = currentPath;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          value = null;
          break;
        }
      }
      
      if (value !== null && value !== undefined) {
        console.log(`Successfully extracted data from path for keys: ${keys.join('.')}`);
        return value;
      }
    }
    
    console.warn(`Could not extract data for keys: ${keys.join('.')}`);
    return null;
  };

  // Function to toggle lambda log sections
  const toggleLogSection = (section) => {
    setExpandedLogSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // FIXED: Fetch lambda logs using the correct field name and show raw response
  const fetchLambdaLogs = async () => {
    if (!microService || !parentAgent?.awsRegion) {
      console.error('Missing required data for lambda logs:', {
        microService: !!microService,
        awsRegion: parentAgent?.awsRegion
      });
      toast.error('Missing required data for lambda logs retrieval');
      return;
    }

    try {
      setFetchingLambdaLogs(true);

      // Determine the correct function name based on environment
      // Since service testing is restricted to development, use dev function name
      let lambdaFunctionName = null;
      
      if (executingEnvironment === 'production' && microService.function_name_prod) {
        lambdaFunctionName = microService.function_name_prod;
      } else if (microService.function_name_dev) {
        lambdaFunctionName = microService.function_name_dev;
      } else {
        // Fallback to the service name if no specific function names available
        lambdaFunctionName = microService.name;
      }

      console.log('Using lambda function name:', lambdaFunctionName);
      console.log('From micro service data:', {
        function_name_dev: microService.function_name_dev,
        function_name_prod: microService.function_name_prod,
        name: microService.name
      });

      // FIXED: Updated payload structure with correct field name
      const payload = {
        lambda_function_name: lambdaFunctionName,
        awsRegion: parentAgent.awsRegion
      };

      console.log('Fetching lambda logs with payload:', payload);

      const response = await api.makeAuthenticatedRequest('POST', '/logs/retrievelambdalogs', payload);
      
      console.log('Lambda logs response:', response);
      
      // FIXED: Store the raw response instead of trying to parse it
      setLambdaLogs(response);
      
      setFetchingLambdaLogs(false);
      toast.success('Lambda logs retrieved successfully');
    } catch (err) {
      console.error('Error fetching lambda logs:', err);
      setFetchingLambdaLogs(false);
      toast.error('Failed to retrieve lambda logs: ' + err.message);
    }
  };

  // ENHANCED: Handle Quick Fix/Enhancement submission with improved UX
  const handleQuickFixSubmit = async () => {
    if (!quickFixInstructions.trim() || quickFixInstructions.trim().length < 10) {
      toast.error('Instructions must be at least 10 characters long');
      return;
    }

    if (!microService || !parentAgent) {
      toast.error('Missing required service or parent agent data');
      return;
    }

    try {
      setSubmittingQuickFix(true);
      setQuickFixResult(null);
      setShowSuccessMessage(false);

      // Build the GitHub repo string from parent agent metadata
      const githubAccount = parentAgent.repoAccount || parentAgent.github_account;
      const githubRepo = parentAgent.repoName || parentAgent.github_repo;
      
      if (!githubAccount || !githubRepo) {
        throw new Error('GitHub repository information not available in parent agent metadata');
      }

      const gitHubRepo = `${githubAccount}/${githubRepo}`;
      
      // Get microservice name from metadata
      const microServiceName = microService.name;
      
      // Build update instructions by appending user input and lambda logs if requested
      let updateInstructions = quickFixInstructions.trim();
      
      if (addLambdaLogsToContext && lambdaLogs) {
        updateInstructions += '\n\nLambda Logs Context:\n';
        updateInstructions += typeof lambdaLogs === 'string' 
          ? lambdaLogs 
          : JSON.stringify(lambdaLogs, null, 2);
      }

      const parentAgentName = parentAgent.agentName;

      // Prepare payload for micro-agent 302
      const payload = {
        gitHubRepo,
        microServiceName,
        parentAgentName,
        updateInstructions
      };

      console.log('Calling micro-agent 302 with payload:', payload);

      // Call micro-agent 302 in production mode using the same process as micro-agent 111
      // Note: We need to find the micro_agent_id for agent 302
      // For now, I'll use a placeholder and the system should resolve it
      const response = await api.testAgent(
        agentId,           // Parent agent ID (AgentDeveloper)
        'micro-agent',     // Agent 302 is a micro-agent
        '48',             // Agent number - this should be resolved to micro_agent_id
        payload,           // The payload with the three required fields
        'production'       // Always run in production
      );

      console.log('Quick fix execution response:', response);

      if (response) {
        setQuickFixResult({
          success: true,
          message: 'Quick fix/enhancement request has been executed successfully!',
          executionArn: response.executionArn || response.body?.executionArn,
          response: response
        });
        
        setShowSuccessMessage(true);
        
        // Clear the form
        setQuickFixInstructions('');
        setAddLambdaLogsToContext(false);
        
        toast.success('Quick fix/enhancement executed successfully!');
        
        // Auto-close modal after 5 seconds (increased time)
        setTimeout(() => {
          setShowQuickFixModal(false);
          setShowSuccessMessage(false);
          setQuickFixResult(null);
        }, 5000);
      } else {
        throw new Error('No response received from quick fix agent');
      }

    } catch (err) {
      console.error('Error submitting quick fix:', err);
      setQuickFixResult({
        success: false,
        error: err.message || 'Failed to submit quick fix request'
      });
      toast.error('Failed to submit quick fix request: ' + err.message);
    } finally {
      setSubmittingQuickFix(false);
    }
  };

  // FIXED: Handle modal close with proper conditions
  const handleModalClose = () => {
    // Only allow closing if not currently processing and not showing success message
    if (!submittingQuickFix && !showSuccessMessage) {
      setShowQuickFixModal(false);
      setQuickFixResult(null);
      setQuickFixInstructions('');
      setAddLambdaLogsToContext(false);
    }
  };

  // ENHANCED: Render Quick Fix/Enhancement Modal with improved styling and UX
  const renderQuickFixModal = () => {
    return (
      <Modal 
        show={showQuickFixModal} 
        onHide={handleModalClose}
        size="lg"
        centered
        backdrop={submittingQuickFix || showSuccessMessage ? "static" : true}
        keyboard={!submittingQuickFix && !showSuccessMessage}
        style={{
          zIndex: 1050
        }}
      >
        <Modal.Header closeButton={!submittingQuickFix && !showSuccessMessage}>
          <Modal.Title>
            <FaWrench className="me-2" />
            {showSuccessMessage ? 'Execution Completed!' : 'Quick Fix / Enhancement'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Success Message Display */}
          {showSuccessMessage && quickFixResult?.success && (
            <div className="text-center py-4">
              <FaCheckCircle size={48} className="text-success mb-3" />
              <h4 className="text-success mb-3">Success!</h4>
              <p className="lead mb-3">{quickFixResult.message}</p>
              {quickFixResult.executionArn && (
                <div className="mb-3">
                  <small className="text-muted">
                    Execution ARN: {quickFixResult.executionArn}
                  </small>
                </div>
              )}
              <div className="text-muted">
                <small>This window will close automatically in a few seconds...</small>
              </div>
            </div>
          )}

          {/* Error Display */}
          {quickFixResult && !quickFixResult.success && !showSuccessMessage && (
            <Alert variant="danger" className="mb-3">
              <strong>Error:</strong> {quickFixResult.error}
            </Alert>
          )}
          
          {/* Form (only show when not showing success message) */}
          {!showSuccessMessage && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Change / Fix Instructions *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={quickFixInstructions}
                  onChange={(e) => setQuickFixInstructions(e.target.value)}
                  placeholder="Enter detailed instructions for what changes to make to the micro service..."
                  disabled={submittingQuickFix}
                  required
                />
                <Form.Text className="text-muted">
                  {quickFixInstructions.length}/10 characters minimum
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Add lambda logs to context"
                  checked={addLambdaLogsToContext}
                  onChange={(e) => setAddLambdaLogsToContext(e.target.checked)}
                  disabled={submittingQuickFix || !lambdaLogs}
                />
                <Form.Text className="text-muted">
                  {!lambdaLogs && 'Lambda logs must be fetched first to use this option'}
                </Form.Text>
              </Form.Group>
              
              {microService && parentAgent && (
                <Alert variant="info" className="mb-3">
                  <h6>Target Information:</h6>
                  <div><strong>GitHub Repository:</strong> {parentAgent.repoAccount || parentAgent.github_account}/{parentAgent.repoName || parentAgent.github_repo}</div>
                  <div><strong>Micro Service:</strong> {microService.name}</div>
                </Alert>
              )}

              {submittingQuickFix && (
                <Alert variant="info" className="mb-3">
                  <div className="d-flex align-items-center">
                    <Spinner size="sm" className="me-3" />
                    <div>
                      <strong>Processing your request...</strong>
                      <div className="small text-muted">This may take a few moments to complete.</div>
                    </div>
                  </div>
                </Alert>
              )}
            </Form>
          )}
        </Modal.Body>
        {!showSuccessMessage && (
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={handleModalClose}
              disabled={submittingQuickFix}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleQuickFixSubmit}
              disabled={submittingQuickFix || quickFixInstructions.trim().length < 10}
            >
              {submittingQuickFix ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Executing...
                </>
              ) : (
                <>
                  <FaWrench className="me-2" />
                  Submit
                </>
              )}
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    );
  };

  // ENHANCED: Render Lambda Logs section with human-readable formatting
  const renderLambdaLogsSection = () => {
    if (!lambdaLogs) return null;

    // Parse the lambda logs response
    const parsedLogs = parseLambdaLogsResponse(lambdaLogs);
    
    if (parsedLogs.error) {
      return (
        <Card className="logs-card mb-4">
          <Card.Header>
            <h2 className="section-heading text-dark">
              <FaServer className="me-2" />
              Lambda Logs
              {fetchingLambdaLogs && <FaSpinner className="fa-spin ms-2" />}
            </h2>
          </Card.Header>
          <Card.Body className="p-3">
            <Alert variant="warning">
              <FaInfoCircle className="me-2" />
              Error parsing lambda logs: {parsedLogs.error}
            </Alert>
            <details>
              <summary className="text-muted">View raw response</summary>
              <div className="p-3 bg-light rounded mt-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <pre className="mb-0" style={{ fontSize: '0.8rem' }}>
                  {typeof lambdaLogs === 'string' ? lambdaLogs : JSON.stringify(lambdaLogs, null, 2)}
                </pre>
              </div>
            </details>
          </Card.Body>
        </Card>
      );
    }

    const { events, metadata, request, status, statusCode } = parsedLogs;

    return (
      <Card className="logs-card mb-4">
        <Card.Header>
          <h2 className="section-heading text-dark">
            <FaServer className="me-2" />
            Lambda Logs
            {fetchingLambdaLogs && <FaSpinner className="fa-spin ms-2" />}
            <Badge bg={status === 'SUCCESS' ? 'success' : 'secondary'} className="ms-2">
              {status || 'UNKNOWN'}
            </Badge>
            <Badge bg={statusCode === 200 ? 'success' : 'secondary'} className="ms-2">
              Status: {statusCode || 'N/A'}
            </Badge>
          </h2>
        </Card.Header>
        <Card.Body className="p-0">
          
          {/* Summary Section */}
          <Card className="border-0 border-bottom">
            <Card.Header 
              className="d-flex justify-content-between align-items-center bg-light"
              style={{ cursor: 'pointer' }}
              onClick={() => toggleLogSection('summary')}
            >
              <div className="d-flex align-items-center">
                <FaInfo className="me-2 text-primary" />
                <strong>Summary</strong>
                <Badge bg="secondary" className="ms-2">
                  {events.length} events
                </Badge>
              </div>
              {expandedLogSections.summary ? <FaChevronDown /> : <FaChevronRight />}
            </Card.Header>
            <Collapse in={expandedLogSections.summary}>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Function Name:</strong> {metadata.function_name || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Log Group:</strong> 
                      <small className="text-muted ms-2">{metadata.resolved_log_group || 'N/A'}</small>
                    </div>
                    <div className="mb-2">
                      <strong>Region:</strong> {metadata.region || 'N/A'}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Events Retrieved:</strong> {metadata.events_returned || events.length}
                    </div>
                    <div className="mb-2">
                      <strong>Pages Scanned:</strong> {metadata.pages_scanned || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Retrieval Strategy:</strong> 
                      <small className="text-muted ms-2">{metadata.retrieval_strategy || 'N/A'}</small>
                    </div>
                  </Col>
                </Row>
                {request && Object.keys(request).length > 0 && (
                  <div className="mt-3">
                    <h6>Request Parameters</h6>
                    <div className="p-2 bg-light rounded">
                      <div><strong>Limit Requested:</strong> {request.limit_requested || 'N/A'}</div>
                      <div><strong>Limit Applied:</strong> {request.limit_applied || 'N/A'}</div>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Collapse>
          </Card>

          {/* Events Section */}
          <Card className="border-0 border-bottom">
            <Card.Header 
              className="d-flex justify-content-between align-items-center bg-light"
              style={{ cursor: 'pointer' }}
              onClick={() => toggleLogSection('events')}
            >
              <div className="d-flex align-items-center">
                <FaList className="me-2 text-success" />
                <strong>Log Events</strong>
                <Badge bg="info" className="ms-2">
                  {events.length} events
                </Badge>
              </div>
              {expandedLogSections.events ? <FaChevronDown /> : <FaChevronRight />}
            </Card.Header>
            <Collapse in={expandedLogSections.events}>
              <Card.Body className="p-0">
                {events.length === 0 ? (
                  <div className="p-4 text-center text-muted">
                    <FaInfoCircle size={24} className="mb-2" />
                    <p>No log events found</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <Table responsive hover className="mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: '160px' }}>Timestamp</th>
                          <th style={{ width: '120px' }}>Ingested</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((event, index) => {
                          const timestamp = formatTimestamp(event.timestamp);
                          const ingestionTime = formatTimestamp(event.ingestionTime);
                          const cleanedMessage = cleanLogMessage(event.message);
                          
                          return (
                            <tr key={index}>
                              <td className="small">
                                <div className="fw-bold">{timestamp.time}</div>
                                <div className="text-muted small">{timestamp.date}</div>
                              </td>
                              <td className="small text-muted">
                                <div>{ingestionTime.time}</div>
                                <div className="small">{ingestionTime.date}</div>
                              </td>
                              <td>
                                <div style={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: '0.85rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: '150px',
                                  overflowY: cleanedMessage.length > 500 ? 'auto' : 'visible'
                                }}>
                                  {cleanedMessage}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Collapse>
          </Card>

          {/* Metadata Section */}
          <Card className="border-0">
            <Card.Header 
              className="d-flex justify-content-between align-items-center bg-light"
              style={{ cursor: 'pointer' }}
              onClick={() => toggleLogSection('metadata')}
            >
              <div className="d-flex align-items-center">
                <FaDatabase className="me-2 text-warning" />
                <strong>Detailed Metadata</strong>
              </div>
              {expandedLogSections.metadata ? <FaChevronDown /> : <FaChevronRight />}
            </Card.Header>
            <Collapse in={expandedLogSections.metadata}>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <h6>Function Information</h6>
                    <div className="p-3 bg-light rounded mb-3">
                      <pre className="mb-0 small">
                        {JSON.stringify(metadata, null, 2)}
                      </pre>
                    </div>
                  </Col>
                  <Col md={6}>
                    <h6>Request Information</h6>
                    <div className="p-3 bg-light rounded mb-3">
                      <pre className="mb-0 small">
                        {JSON.stringify(request, null, 2)}
                      </pre>
                    </div>
                    
                    <h6 className="mt-3">Raw Response</h6>
                    <details>
                      <summary className="text-muted small">View complete raw response</summary>
                      <div className="p-3 bg-light rounded mt-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <pre className="mb-0 small">
                          {typeof lambdaLogs === 'string' ? lambdaLogs : JSON.stringify(lambdaLogs, null, 2)}
                        </pre>
                      </div>
                    </details>
                  </Col>
                </Row>
              </Card.Body>
            </Collapse>
          </Card>

        </Card.Body>
      </Card>
    );
  };

  // Fetch the micro service details and parent agent details
  const fetchMicroServiceDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch parent agent details with enhanced environment-specific fields
      try {
        console.log('Fetching parent agent details with environment-specific fields...');
        const agentsResponse = await api.getAgents();
        
        if (agentsResponse && agentsResponse.success && agentsResponse.agents) {
          const parentAgentData = agentsResponse.agents.find(a => 
            a.uniqueIdentifier === agentId || 
            a.agentName === agentId || 
            a.repoName === agentId
          );
          
          if (parentAgentData) {
            console.log('Parent agent data loaded with environment fields:', parentAgentData);
            setParentAgent(parentAgentData);
          } else {
            console.warn('Parent agent not found in response');
            // Set fallback parent agent with minimal fields
            setParentAgent({
              id: agentId,
              name: "AgentDeveloper",
              repoName: "AgentDeveloper",
              awsAccount: "851725399969",
              awsRegion: "us-west-2",
              repoAccount: "materassi"
            });
          }
        }
      } catch (err) {
        console.error('Error fetching parent agent details:', err);
        // Set fallback parent agent
        setParentAgent({
          id: agentId,
          name: "AgentDeveloper",
          repoName: "AgentDeveloper",
          awsAccount: "851725399969",
          awsRegion: "us-west-2",
          repoAccount: "materassi"
        });
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching micro service details:', err);
      setError('Failed to load micro service details');
      setLoading(false);
    }
  };

  // ENHANCED: Helper function to get the correct user pool id based on environment
  const getUserPoolId = (environment) => {
    if (!parentAgent) {
      console.warn('Parent agent data not available for user pool lookup');
      return "us-west-2_ZZ7KIcFFj"; // fallback
    }

    const env = environment.toLowerCase();
    console.log(`Getting user pool ID for environment: ${env}`);

    if (env === 'development' || env === 'dev') {
      const userPoolId = parentAgent.api_user_pool_id_dev || "us-west-2_oj6UxcxeB";
      console.log('Using development user pool ID:', userPoolId);
      return userPoolId;
    } else if (env === 'production' || env === 'prod') {
      const userPoolId = parentAgent.api_user_pool_id_prod || "us-west-2_ZZ7KIcFFj";
      console.log('Using production user pool ID:', userPoolId);
      return userPoolId;
    }

    console.warn(`Unknown environment: ${environment}, defaulting to development user pool`);
    return parentAgent.api_user_pool_id_dev || "us-west-2_oj6UxcxeB";
  };

  // ENHANCED: Helper function to get the correct client id based on environment
  const getClientId = (environment) => {
    if (!parentAgent) return "7qlbjda3i0qprm7igckv2t6gmn"; // fallback
    
    const env = environment.toLowerCase();
    if (env === 'development' || env === 'dev') {
      return parentAgent.api_user_pool_client_dev || "6bkduujqoiiiqv3tga45mvki2o";
    } else if (env === 'production' || env === 'prod') {
      return parentAgent.api_user_pool_client_prod || "7qlbjda3i0qprm7igckv2t6gmn";
    }
    return "7qlbjda3i0qprm7igckv2t6gmn"; // fallback
  };

  // ENHANCED: Helper function to get the correct identity pool id based on environment
  const getIdentityPoolId = (environment) => {
    if (!parentAgent) return "us-west-2:d4938532-5c88-4b35-9cbd-8c887400e6af"; // fallback
    
    const env = environment.toLowerCase();
    if (env === 'development' || env === 'dev') {
      return parentAgent.api_identity_pool_dev || "us-west-2:1392f14a-92aa-48b6-af87-25bb1e881f0f";
    } else if (env === 'production' || env === 'prod') {
      return parentAgent.api_identity_pool_prod || "us-west-2:d4938532-5c88-4b35-9cbd-8c887400e6af";
    }
    return "us-west-2:d4938532-5c88-4b35-9cbd-8c887400e6af"; // fallback
  };

  // ENHANCED: Helper function to construct cf_domain based on environment
  const getCfDomain = (environment) => {
    if (!parentAgent) return "f02ij0peld.agent-enterprise.com"; // fallback
    
    const env = environment.toLowerCase();
    console.log(`Constructing cf_domain for environment: ${env}`);
    
    if (env === 'development' || env === 'dev') {
      const subdomain = parentAgent.api_subdomain_dev;
      const domain = parentAgent.api_domain_dev;
      
      if (subdomain && domain) {
        const cfDomain = `${subdomain}.${domain}`;
        console.log('Constructed development cf_domain:', cfDomain);
        return cfDomain;
      }
    } else if (env === 'production' || env === 'prod') {
      const subdomain = parentAgent.api_subdomain_prod;
      const domain = parentAgent.api_domain_prod;
      
      if (subdomain && domain) {
        const cfDomain = `${subdomain}.${domain}`;
        console.log('Constructed production cf_domain:', cfDomain);
        return cfDomain;
      }
    }
    
    console.warn(`Could not construct cf_domain for environment ${environment}, using fallback`);
    return "f02ij0peld.agent-enterprise.com";
  };

  // ENHANCED: Helper function to get test credentials based on environment
  const getTestCredentials = (environment) => {
    if (!parentAgent) {
      console.warn('Parent agent data not available for test credentials');
      return {
        username: "materassi@gmail.com",
        password: "jixfyt-kefhop-gEjde4"
      };
    }
    
    const env = environment.toLowerCase();
    console.log(`Getting test credentials for environment: ${env}`);
    
    if (env === 'development' || env === 'dev') {
      const username = parentAgent.api_test_dev_username;
      const password = parentAgent.api_test_dev_password;
      
      if (username && password) {
        console.log('Using development test credentials for user:', username);
        return { username, password };
      }
    } else if (env === 'production' || env === 'prod') {
      const username = parentAgent.api_test_prod_username;
      const password = parentAgent.api_test_prod_password;
      
      if (username && password) {
        console.log('Using production test credentials for user:', username);
        return { username, password };
      }
    }
    
    console.warn(`Could not get test credentials for environment ${environment}, using fallback`);
    return {
      username: "materassi@gmail.com", 
      password: "jixfyt-kefhop-gEjde4"
    };
  };

  // Helper function to check if field name requires large text box
  const isLargeTextField = (fieldName) => {
    const lowerFieldName = fieldName.toLowerCase().replace(/[_\s]/g, '');
    const largeTextFields = ['instructions', 'architecture', 'description', 'task', 'maintask', 'main_task'];
    return largeTextFields.some(field => lowerFieldName.includes(field.replace(/[_\s]/g, '')));
  };

  // Helper function to flatten nested objects into form fields
  const flattenObjectToFields = (obj, prefix = '', properties = {}, required = []) => {
    const fields = [];
    
    Object.keys(obj).forEach(key => {
      const fullPath = prefix ? `${prefix}.${key}` : key;
      const propSchema = properties[key] || {};
      const value = obj[key];
      
      // Check if this is a nested object (but not an array)
      if (value && typeof value === 'object' && !Array.isArray(value) && propSchema.type === 'object') {
        // Add a visual separator/grouping for nested objects
        fields.push({
          name: fullPath,
          label: propSchema.title || key,
          type: 'object',
          isGroup: true,
          description: propSchema.description,
          required: required?.includes(key)
        });
        
        // Recursively flatten nested properties
        const nestedProps = propSchema.properties || {};
        const nestedRequired = propSchema.required || [];
        const nestedFields = flattenObjectToFields(value, fullPath, nestedProps, nestedRequired);
        fields.push(...nestedFields);
      } else {
        // Regular field - Include parent name in label if nested
        let fieldLabel = propSchema.title || key;
        
        // If this is a nested field (has a prefix), include parent name in label
        if (prefix) {
          // Get the immediate parent name from the prefix
          const parentParts = prefix.split('.');
          const parentName = parentParts[parentParts.length - 1];
          // Capitalize and format parent name
          const parentLabel = parentName.charAt(0).toUpperCase() + parentName.slice(1).replace(/_/g, ' ');
          // Ensure field label is also properly formatted
          const childLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1).replace(/_/g, ' ');
          fieldLabel = `${parentLabel} - ${childLabel}`;
        }
        
        fields.push({
          name: fullPath,
          label: fieldLabel,
          type: propSchema.type || (Array.isArray(value) ? 'array' : typeof value),
          format: propSchema.format,
          description: propSchema.description,
          required: required?.includes(key),
          default: propSchema.default,
          enum: propSchema.enum,
          options: propSchema.options
        });
      }
    });
    
    return fields;
  };

  // Fetch the micro service schema from the db/micro-services endpoint
  const fetchSchema = async () => {
    try {
      setLoading(true);
      console.log('Fetching schema from db/micro-services endpoint for microServiceId:', microServiceId);
      
      // Call the db/micro-services endpoint with the micro_service_id
      const response = await api.makeAuthenticatedRequest('POST', '/db/micro-services', {
        micro_service_id: microServiceId
      });
      
      console.log('Raw response from db/micro-services:', response);
      
      // Extract micro_service object from the nested response structure
      const microServiceData = extractFromResponse(response, 'data', 'micro_service');
      
      if (!microServiceData) {
        throw new Error('No micro-service found in response');
      }
      
      console.log('Found micro-service:', microServiceData);
      
      // FIXED: Set the micro service data including function names
      setMicroService({
        id: microServiceData.micro_service_id,
        name: microServiceData.name,
        description: microServiceData.description,
        endpoint: microServiceData.endpoint,
        method: microServiceData.method,
        devStatus: microServiceData.deployed_development ? 'deployed' : 'not deployed',
        prodStatus: microServiceData.deployed_production ? 'deployed' : 'not deployed',
        status: microServiceData.status,
        active: microServiceData.active,
        agent_id: microServiceData.agent_id,
        agent_name: microServiceData.agent_name,
        scope: microServiceData.scope,
        documentation: microServiceData.documentation,
        environment_variables: microServiceData.environment_variables,
        permissions: microServiceData.permissions,
        // FIXED: Include function names for lambda logs
        function_name_dev: microServiceData.function_name_dev,
        function_name_prod: microServiceData.function_name_prod
      });
      
      // Extract and parse the schema field - ENHANCED to handle multiple formats
      if (microServiceData.input_output_schema || microServiceData.schema) {
        let schemaObject;
        const schemaField = microServiceData.input_output_schema || microServiceData.schema;
        
        try {
          console.log('Schema field type:', typeof schemaField);
          console.log('Schema field preview:', 
            typeof schemaField === 'string' ? schemaField.substring(0, 100) : schemaField
          );
          
          // Check if schema is already an object
          if (typeof schemaField === 'object') {
            console.log('Schema is already an object, processing directly');
            schemaObject = processSchemaObject(schemaField);
          } 
          // Check if schema is a string (could be plain JSON or markdown)
          else if (typeof schemaField === 'string') {
            console.log('Schema is a string, attempting enhanced parsing');
            // Use enhanced parser that handles both plain JSON and markdown formats
            schemaObject = parseSchemaFromString(schemaField);
            console.log('Successfully parsed schema using enhanced parser:', schemaObject);
          } else {
            console.error('Schema is neither object nor string:', typeof schemaField);
            throw new Error('Invalid schema format - unsupported data type');
          }
          
          // Process conditional fields (like coreDeploymentFields/agentDeploymentFields)
          schemaObject = processConditionalFields(schemaObject);
          
        } catch (parseError) {
          console.error('Error parsing schema:', parseError);
          console.log('Schema that failed to parse:', schemaField);
          throw new Error('Invalid schema format - could not parse: ' + parseError.message);
        }
        
        // Validate that we have a proper schema
        if (schemaObject && schemaObject.properties) {
          setSchema(schemaObject);
          
          // Initialize form data with proper default values based on field types
          const initialData = {};
          Object.keys(schemaObject.properties).forEach(key => {
            const prop = schemaObject.properties[key];
            
            // Set default value based on type to prevent validation failures
            if (prop.type === 'object' && prop.properties) {
              // Initialize nested object with proper defaults for all nested fields
              initialData[key] = initializeNestedObject(prop.properties, prop.required);
            } else {
              // Use type-appropriate default value
              initialData[key] = getDefaultValueForType(prop.type, prop);
            }
          });
          
          console.log('Initial form data with type-safe defaults:', initialData);
          
          // Flatten nested objects into individual fields
          const fields = flattenObjectToFields(
            initialData, 
            '', 
            schemaObject.properties, 
            schemaObject.required
          );
          
          setFormFields(fields);
          setFormData(initialData);
          
          console.log('Schema successfully processed. Form fields:', fields.length);
        } else {
          console.warn('Processed schema is missing properties, creating fallback');
          throw new Error('Processed schema is invalid - no properties found');
        }
      } else {
        // If no schema, create a simple default form
        console.log('No schema found in micro-service, creating default form');
        const defaultSchema = {
          type: 'object',
          properties: {
            data: {
              type: 'string',
              title: 'Test Data',
              description: 'Enter test data for the micro service'
            }
          },
          required: []
        };
        
        setSchema(defaultSchema);
        setFormFields([{
          name: 'data',
          label: 'Test Data',
          type: 'string',
          description: 'Enter test data for the micro service',
          required: false
        }]);
        setFormData({ data: '' });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching schema:', err);
      setError('Failed to load micro service schema: ' + err.message);
      setLoading(false);
    }
  };

  // Handle form field changes - Enhanced to handle nested paths
  const handleInputChange = (name, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      
      // Handle nested field paths (e.g., "parent.child")
      if (name.includes('.')) {
        const parts = name.split('.');
        let current = newData;
        
        // Navigate to the nested property
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }
        
        // Set the final value
        current[parts[parts.length - 1]] = value;
      } else {
        // Simple property
        newData[name] = value;
      }
      
      return newData;
    });
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Get nested value from form data
  const getNestedValue = (obj, path) => {
    if (!path.includes('.')) {
      return obj[path];
    }
    
    const parts = path.split('.');
    let value = obj;
    
    for (const part of parts) {
      if (value && typeof value === 'object') {
        value = value[part];
      } else {
        return undefined;
      }
    }
    
    return value;
  };

  // Validate the form before submission
  const validateForm = () => {
    const errors = {};
    formFields.filter(field => field.required && !field.isGroup).forEach(field => {
      const value = getNestedValue(formData, field.name);
      // Updated validation to handle default values (false, 0, [], {}) as valid
      if (value === undefined || value === null || value === '') {
        errors[field.name] = `${field.label || field.name} is required`;
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ENHANCED: Handle form submission with proper payload structure using parent agent fields
  const handleSubmit = async (env) => {
    if (!validateForm()) return;
  
    setExecutingEnvironment(env);
    setExecuting(true);
    setError(null);
    setResult(null);
    setExecutionArn(null);
    setLiveLogs(null);
    // NEW: Clear previous lambda logs
    setLambdaLogs(null);
  
    try {
      console.log('Testing micro service with payload:', formData);
      console.log('Using environment:', env);
      
      // Get environment-specific values from parent agent
      const userPoolId = getUserPoolId(env);
      const clientId = getClientId(env);
      const identityPoolId = getIdentityPoolId(env);
      const cfDomain = getCfDomain(env);
      const testCredentials = getTestCredentials(env);
      
      console.log('Environment-specific configuration:', {
        environment: env,
        cfDomain,
        userPoolId,
        clientId,
        identityPoolId,
        username: testCredentials.username
      });
      
      // ENHANCED: Structure payload according to the specification using parent agent fields
      const payload = {
        cf_domain: cfDomain,
        method: microService.method,
        endpoint: microService.endpoint,
        username: testCredentials.username,
        password: testCredentials.password,
        identity_pool_id: identityPoolId,
        user_pool_id: userPoolId,
        client_id: clientId,
        payload: formData, // This is the nested payload object containing form data
        region: parentAgent?.awsRegion || "us-west-2"
      };
      
      console.log('Structured payload for micro service test:', payload);
      
      // Test the micro service using the test/micro-service endpoint
      const response = await api.makeAuthenticatedRequest('POST', '/test/micro-service', payload);
      
      console.log('Micro service test response:', response);
      
      if (response) {
        // Extract the result from the nested response structure
        const testResults = extractFromResponse(response, 'data', 'test_results');
        
        if (testResults) {
          // Structure the result with proper success detection
          const structuredResult = {
            endpoint_status: testResults.endpoint_status,
            endpoint_success: testResults.endpoint_success,
            endpoint_response: testResults.endpoint_response,
            test_metadata: extractFromResponse(response, 'data', 'test_metadata'),
            authentication: extractFromResponse(response, 'data', 'authentication'),
            input_parameters: extractFromResponse(response, 'data', 'input_parameters'),
            // Add the raw response for fallback success detection
            success: response.success,
            data: response.data
          };
          setResult(structuredResult);
        } else {
          // If no test_results structure found, use the entire response
          // Add success detection for responses without test_results structure
          const enhancedResult = {
            ...response,
            endpoint_success: isResponseSuccessful(response),
            endpoint_status: getResponseStatusCode(response) || 200,
            endpoint_response: getResponseBody(response)
          };
          setResult(enhancedResult);
        }
        
        // Extract executionArn if available (for future polling support)
        const extractedArn = extractFromResponse(response, 'executionArn');
        if (extractedArn) {
          console.log('Execution ARN extracted:', extractedArn);
          setExecutionArn(extractedArn);
        }
        
        toast.success(`Service test in ${env} completed successfully`);
      }
    } catch (err) {
      console.error('Error testing micro service:', err);
      setError(`Failed to test micro service: ${err.message}`);
      toast.error('Service test failed');
    } finally {
      setExecuting(false);
    }
  };

  // Navigation handlers
  const handleGoBackToService = () => {
    navigate(`/agents/${agentId}/micro-services/${microServiceId}`);
  };

  const handleGoBackToParent = () => {
    navigate(`/parent/${agentId}`);
  };

  // Render loading state
  if (loading && !microService) {
    return (
      <div className="agent-testing-page loading-container">
        <Spinner />
        <p>Loading micro service details...</p>
      </div>
    );
  }

  // Format JSON for display
  const formatJson = (obj) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return String(obj);
    }
  };

  return (
    <div className="agent-testing-page">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header with Service Info */}
      <div className="agent-header">
        <div className="agent-title-container">
          <h1 className="agent-title">Test Micro Service</h1>
          <p className="agent-subtitle">
            Service: {microService?.name || microServiceId}
            {microService?.endpoint && <span> | Endpoint: {microService.endpoint}</span>}
            {microService?.method && <span> | Method: {microService.method}</span>}
            {environment && <span> | Environment: {environment}</span>}
          </p>
        </div>
        
        <div className="agent-actions">
          <Button 
            variant="outline-primary" 
            onClick={handleGoBackToService}
            className="action-button"
          >
            <FaArrowLeft className="me-2" />
            Back to Service
          </Button>
          
          <Button 
            variant="outline-primary" 
            onClick={handleGoBackToParent}
            className="action-button"
          >
            <FaServer className="me-2" />
            Back to Parent Agent
          </Button>
        </div>
      </div>
      
      {/* Service Details Card */}
      {microService && (
        <Card className="agent-info-card mb-4">
          <Card.Header>
            <h2 className="section-heading text-dark">Service Information</h2>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="agent-info-details">
                  <div className="info-item">
                    <div className="info-label">Name:</div>
                    <div className="info-value">{microService.name}</div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">Description:</div>
                    <div className="info-value" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                      {microService.description}
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">Parent Agent:</div>
                    <div className="info-value">{parentAgent?.name || microService.agent_name}</div>
                  </div>
                </div>
              </Col>
              
              <Col md={6}>
                <div className="agent-info-details">
                  <div className="info-item">
                    <div className="info-label">Service ID:</div>
                    <div className="info-value">{microService.id}</div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">Endpoint:</div>
                    <div className="info-value"><code>{microService.endpoint}</code></div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">HTTP Method:</div>
                    <div className="info-value">
                      <Badge bg="info">{microService.method}</Badge>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">Development Status:</div>
                    <div className="info-value">
                      <Badge bg={microService.devStatus === 'deployed' ? 'success' : 'secondary'}>
                        {microService.devStatus || 'Not Deployed'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">Production Status:</div>
                    <div className="info-value">
                      <Badge bg={microService.prodStatus === 'deployed' ? 'success' : 'secondary'}>
                        {microService.prodStatus || 'Not Deployed'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      
      {/* Debug Information for Parent Agent Configuration */}
      {parentAgent && (parentAgent.api_user_pool_id_dev || parentAgent.api_user_pool_id_prod) && (
        <Alert variant="info" className="mb-4">
          <FaInfoCircle className="me-2" />
          Environment Configuration Status:
          <div className="mt-2">
            <Row>
              <Col md={6}>
                <strong>Development Environment:</strong>
                <div><small>Domain: {parentAgent.api_domain_dev || 'Not configured'}</small></div>
                <div><small>Subdomain: {parentAgent.api_subdomain_dev || 'Not configured'}</small></div>
                <div><small>User Pool: {parentAgent.api_user_pool_id_dev || 'Not configured'}</small></div>
                <div><small>Test User: {parentAgent.api_test_dev_username || 'Not configured'}</small></div>
              </Col>
              <Col md={6}>
                <strong>Production Environment:</strong>
                <div><small>Domain: {parentAgent.api_domain_prod || 'Not configured'}</small></div>
                <div><small>Subdomain: {parentAgent.api_subdomain_prod || 'Not configured'}</small></div>
                <div><small>User Pool: {parentAgent.api_user_pool_id_prod || 'Not configured'}</small></div>
                <div><small>Test User: {parentAgent.api_test_prod_username || 'Not configured'}</small></div>
              </Col>
            </Row>
          </div>
        </Alert>
      )}
      
      {/* Input Parameters Card */}
      <Card className="test-card mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="section-heading text-dark">Input Parameters</h2>
            {schema && (
              <Badge bg="info" className="schema-info">
                {formFields.length} field{formFields.length !== 1 ? 's' : ''} available
              </Badge>
            )}
          </div>
        </Card.Header>
        
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              <FaInfoCircle className="me-2" />
              {error}
            </Alert>
          )}
          
          {!schema && !error && (
            <Alert variant="info" className="mb-3">
              <FaInfoCircle className="me-2" />
              No schema available for this micro service. You can still test it with custom input data.
            </Alert>
          )}
          
          <Form>
            <Row>
              {formFields.map(field => {
                // Skip group headers in form rendering
                if (field.isGroup) {
                  return (
                    <Col md={12} key={field.name} className="mb-3">
                      <h5 className="text-primary border-bottom pb-2">
                        {field.label}
                      </h5>
                      {field.description && (
                        <p className="text-muted small mb-3">{field.description}</p>
                      )}
                    </Col>
                  );
                }
                
                // Determine if this should be a large text field
                const isLarge = isLargeTextField(field.name) || field.format === 'textarea';
                const columnSize = isLarge ? 12 : 6;
                
                return (
                  <Col md={columnSize} key={field.name} className="mb-3">
                    <Form.Group>
                      <Form.Label className={field.required ? 'required-field' : ''}>
                        {field.label}
                      </Form.Label>
                      
                      {renderFormField(field, formData, handleInputChange, formErrors, getNestedValue)}
                      
                      {field.description && (
                        <Form.Text className="field-description">
                          <FaInfoCircle className="me-1" />
                          {field.description}
                        </Form.Text>
                      )}
                      
                      {formErrors[field.name] && (
                        <div className="field-error">{formErrors[field.name]}</div>
                      )}
                    </Form.Group>
                  </Col>
                );
              })}
            </Row>
            
            <div className="execute-buttons">
              {/* REMOVED PRODUCTION OPTION - Only development testing allowed */}
              <Button
                variant="primary"
                onClick={() => handleSubmit('development')}
                disabled={executing}
                className="execute-btn"
              >
                {executing && executingEnvironment === 'development' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Testing in Dev...
                  </>
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    Execute in Development
                  </>
                )}
              </Button>
              
              <div className="mt-2">
                <small className="text-muted">
                  <FaInfoCircle className="me-1" />
                  Production testing is not available for safety reasons. Use development environment for testing.
                </small>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>
      
      {/* Results Section */}
      {result && (
        <Card className="result-card mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="section-heading text-dark">
                Test Results
                <span className="ms-2">
                  <Badge bg={isResponseSuccessful(result) ? 'success' : 'danger'}>
                    {isResponseSuccessful(result) ? 'SUCCESS' : 'FAILED'}
                  </Badge>
                </span>
              </h2>
              {/* UPDATED: Added Quick Fix/Enhancement button alongside Lambda Logs button */}
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-info" 
                  size="sm"
                  onClick={fetchLambdaLogs}
                  disabled={fetchingLambdaLogs || !microService?.function_name_dev}
                >
                  {fetchingLambdaLogs ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaList className="me-2" />
                      Get Lambda Logs
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline-warning" 
                  size="sm"
                  onClick={() => {
                    setQuickFixInstructions('');
                    setAddLambdaLogsToContext(false);
                    setQuickFixResult(null);
                    setShowSuccessMessage(false);
                    setShowQuickFixModal(true);
                  }}
                >
                  <FaWrench className="me-2" />
                  Quick fix/ enhancement
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-4">
                  <h5>Response Summary</h5>
                  <div className="p-3 bg-light rounded">
                    <div className="mb-2">
                      <strong>Status Code:</strong>{' '}
                      {(() => {
                        const statusCode = getResponseStatusCode(result);
                        return statusCode ? (
                          <Badge bg={statusCode === 200 ? 'success' : statusCode >= 200 && statusCode < 300 ? 'success' : 'warning'}>
                            {statusCode}
                          </Badge>
                        ) : (
                          <span className="text-muted">Not available</span>
                        );
                      })()}
                    </div>
                    <div className="mb-2">
                      <strong>Success:</strong>{' '}
                      <Badge bg={isResponseSuccessful(result) ? 'success' : 'danger'}>
                        {isResponseSuccessful(result) ? 'YES' : 'NO'}
                      </Badge>
                    </div>
                    {result.test_metadata && (
                      <>
                        <div className="mb-2">
                          <strong>URL Tested:</strong>{' '}
                          <small>{result.test_metadata.url_tested}</small>
                        </div>
                        <div className="mb-2">
                          <strong>Method Used:</strong>{' '}
                          <Badge bg="info">{result.test_metadata.method_used}</Badge>
                        </div>
                      </>
                    )}
                    {result.authentication && (
                      <div className="mb-2">
                        <strong>Authentication:</strong>{' '}
                        <Badge bg={result.authentication.cognito_auth_successful ? 'success' : 'danger'}>
                          {result.authentication.cognito_auth_successful ? 'Successful' : 'Failed'}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              
              <Col md={6}>
                <div className="mb-4">
                  <h5>Response Headers</h5>
                  <div className="p-3 bg-light rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {(() => {
                      const headers = getResponseHeaders(result);
                      return headers ? (
                        <pre className="mb-0 small">
                          {formatJson(headers)}
                        </pre>
                      ) : (
                        <small className="text-muted">No headers available</small>
                      );
                    })()}
                  </div>
                </div>
              </Col>
            </Row>
            
            <div className="mb-4">
              <h5>Response Body</h5>
              <div className="p-3 bg-light rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <pre className="mb-0">
                  {(() => {
                    const responseBody = getResponseBody(result);
                    return responseBody ? formatJson(responseBody) : formatJson(result);
                  })()}
                </pre>
              </div>
            </div>
            
            {result.test_metadata && (
              <div className="mb-4">
                <h5>Test Metadata</h5>
                <div className="p-3 bg-light rounded">
                  <pre className="mb-0">
                    {formatJson(result.test_metadata)}
                  </pre>
                </div>
              </div>
            )}
            
            {result.input_parameters && (
              <div className="mb-4">
                <h5>Input Parameters</h5>
                <div className="p-3 bg-light rounded">
                  <pre className="mb-0">
                    {formatJson(result.input_parameters)}
                  </pre>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
      
      {/* ENHANCED: Lambda Logs Section with human-readable formatting */}
      {renderLambdaLogsSection()}
      
      {/* Live Logs Section (if executionArn is available) */}
      {executionArn && (
        <Card className="logs-card">
          <Card.Header>
            <h2 className="section-heading text-dark">
              Execution Details
              <span className="execution-id ms-2">
                ID: {executionArn.split(':').pop()}
              </span>
              {fetchingLogs && <FaSpinner className="fa-spin ms-2" />}
            </h2>
          </Card.Header>
          <Card.Body className="p-0">
            <Row className="g-0 execution-details-row">
              {/* Left Column - Live Logs */}
              <Col lg={8} className="logs-column">
                <div className="logs-section-header">
                  <h3 className="section-title">Live Execution Logs</h3>
                </div>
                <div className="logs-content">
                  <ExecutionLogsWindow 
                    executionArn={executionArn}
                    agentId={agentId}
                    targetType="micro-service"
                    result={liveLogs}
                    refreshInterval={5000}
                  />
                </div>
              </Col>
              
              {/* Right Column - Overall Output Panel */}
              <Col lg={4} className="overall-output-column">
                <OverallOutputPanel 
                  executionResult={result}
                  onPanelViewed={handlePanelViewed}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ENHANCED: Quick Fix/Enhancement Modal with improved UX */}
      {renderQuickFixModal()}
    </div>
  );
};

// Helper function to render form fields based on their type - Enhanced for nested fields
const renderFormField = (field, formData, handleInputChange, formErrors, getNestedValue) => {
  const value = getNestedValue(formData, field.name) || '';
  const isInvalid = !!formErrors[field.name];
  
  // Determine if this should be a large text area
  const isLargeField = field.name.toLowerCase().includes('instructions') ||
                      field.name.toLowerCase().includes('architecture') ||
                      field.name.toLowerCase().includes('description') ||
                      field.name.toLowerCase().includes('task') ||
                      field.name.toLowerCase().replace(/[_\s]/g, '').includes('maintask') ||
                      field.name.toLowerCase().replace(/[_\s]/g, '').includes('main_task') ||
                      field.format === 'textarea' ||
                      field.name.toLowerCase().includes('prompt');
  
  switch (field.type) {
    case 'boolean':
      return (
        <Form.Check
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => handleInputChange(field.name, e.target.checked)}
          isInvalid={isInvalid}
          label={field.label}
        />
      );
      
    case 'number':
    case 'integer':
      return (
        <Form.Control
          type="number"
          value={value}
          onChange={(e) => handleInputChange(field.name, e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value)}
          isInvalid={isInvalid}
          placeholder={`Enter ${field.label}`}
          step={field.type === 'integer' ? 1 : 'any'}
        />
      );
      
    case 'array':
      return (
        <Form.Control
          as="textarea"
          rows={4}
          value={Array.isArray(value) ? value.join('\n') : value}
          onChange={(e) => handleInputChange(field.name, e.target.value.split('\n').map(line => line.trim()).filter(Boolean))}
          isInvalid={isInvalid}
          placeholder="Enter one item per line"
        />
      );
      
    case 'object':
      return (
        <Form.Control
          as="textarea"
          rows={6}
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              handleInputChange(field.name, parsed);
            } catch (err) {
              handleInputChange(field.name, e.target.value);
            }
          }}
          isInvalid={isInvalid}
          placeholder={`Enter ${field.label} as JSON`}
          className="json-input"
        />
      );
      
    default:
      if (field.enum) {
        return (
          <Form.Select
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
          >
            <option value="">Select {field.label}</option>
            {field.enum.map((option, index) => (
              <option key={index} value={option}>
                {field.options && field.options[index] ? field.options[index] : option}
              </option>
            ))}
          </Form.Select>
        );
      }
      
      if (isLargeField) {
        return (
          <Form.Control
            as="textarea"
            rows={isLargeField ? 8 : 6}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            placeholder={`Enter ${field.label}`}
          />
        );
      }
      
      return (
        <Form.Control
          type={field.format === 'email' ? 'email' : field.format === 'password' ? 'password' : 'text'}
          value={value}
          onChange={(e) => handleInputChange(field.name, e.target.value)}
          isInvalid={isInvalid}
          placeholder={`Enter ${field.label}`}
        />
      );
  }
};

export default ServiceTestingPage;
